<template>
  <div
    class="text-field-resizable"
    :class="{'resizable-textarea': textarea}">
    <m-text-field
      v-model="textVal"
      :autofocus="autofocus"
      :type="type"
      v-bind="$attrs"
      :textarea="textarea"
      :use-native-validation="false"
      v-on="$listeners"
      @change="$emit('update:value', textVal)"
      @keydown="handleKeydown"
      @keyup="$emit('update:value', textVal)">
      <m-icon
        v-if="leadingIcon"
        slot="leadingIcon"
        class="leading-icon"
        :icon="leadingIcon" />
      <m-icon
        v-if="trailingIcon"
        slot="trailingIcon"
        class="trailing-icon"
        :icon="trailingIcon" />
      <m-floating-label
        v-if="label">
        {{ label }}
      </m-floating-label>
    </m-text-field>
    <m-text-field-helper-text
      v-if="helper"
      :validation-msg="validationMsg"
      :persistent="persistent">
      {{ helper }}
    </m-text-field-helper-text>
  </div>
</template>

<script>
import Vue from 'vue'
import FloatingLabel from 'material-components-vue/dist/floating-label'
import TextField from 'material-components-vue/dist/text-field'
import Icon from 'material-components-vue/dist/icon'

import autosize from 'autosize/dist/autosize.js'

Vue.use(FloatingLabel)
Vue.use(TextField)
Vue.use(Icon)

export default {
  inheritAttrs: true,
  props: {
    allowNewline: { default: false, required: false, type: Boolean },
    autofocus: { default: false, required: false, type: Boolean },
    handleSubmit: { default: () => {}, required: false, type: Function },
    helper: { default: '', required: false, type: String },
    icon: { default: '', required: false, type: String },
    label: { default: '', required: false, type: String },
    leadingIcon: { default: '', required: false, type: String },
    persistent: { default: false, required: false, type: Boolean },
    textarea: { required: false, default: false, type: Boolean },
    trailingIcon: { default: '', required: false, type: String },
    type: { default: 'text', required: false, type: String },
    validationMsg: { default: false, required: false, type: Boolean },
    value: { required: true, type: [Date, Number, Object, String] }
  },
  data () {
    return {
      textVal: this.value
    }
  },
  watch: {
    value () {
      this.textVal = this.value
    }
  },
  mounted () {
    if (this.textarea) autosize(document.querySelectorAll('textarea'))
  },
  methods: {
    handleKeydown (e) {
      const enter = 13
      if (e.keyCode === enter && !this.allowNewline) e.preventDefault()
      if (e.keyCode === enter) this.handleSubmit()
    }
  }
}
</script>

<style lang="scss">
  @import '../../universal/vars.scss';
  @import "material-components-vue/dist/text-field/styles";
  @import "@material/textfield/_mixins.scss";
  @import "@material/floating-label/_mixins.scss";

  .text-field-resizable .mdc-text-field--focused {
    @include mdc-text-field-bottom-line-color($mdc-theme-primary);
    @include mdc-text-field-hover-bottom-line-color($mdc-theme-primary);
  }
  .text-field-resizable .mdc-text-field--focused.mdc-text-field--invalid {
    @include mdc-text-field-bottom-line-color($mdc-theme-error);
    @include mdc-text-field-hover-bottom-line-color($mdc-theme-error);
  }
  .text-field-resizable .mdc-text-field--textarea .mdc-floating-label {
    top: 20px;
  }
  .text-field-resizable .mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-floating-label {
    @include mdc-floating-label-ink-color($mdc-theme-primary);
  }
  .text-field-resizable .mdc-text-field--invalid.mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-floating-label {
      @include mdc-floating-label-ink-color($mdc-theme-error);
    }
  .text-field-resizable .mdc-text-field + .mdc-text-field-helper-line {
    padding-right: 0;
    padding-left: 12px;
  }
  .text-field-resizable .mdc-text-field {
    width: 100%;
    @include mdc-text-field-caret-color($mdc-theme-primary)
  }

  // Text Field Icons
  .text-field-resizable .mdc-text-field--with-leading-icon, .mdc-text-field--with-trailing-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    .material-icons {
      color: $black-high-emphasis;
      font-family: 'Material Icons';
      font-style: normal;
      width: 56px;
      position: absolute;
    }
  }

  // Leading Icon Styles
  .text-field-resizable .mdc-text-field--with-leading-icon.mdc-text-field--with-trailing-icon .mdc-text-field__input,
  .text-field-resizable .mdc-text-field--with-leading-icon .mdc-text-field__input {
    padding-left: 56px;
  }
  .text-field-resizable .mdc-text-field--with-leading-icon .mdc-floating-label {
    left: 56px;
  }
  .text-field-resizable .leading-icon {
    left: 0;
  }
  // Trailing Icon Styles
  .text-field-resizable .mdc-text-field--with-leading-icon.mdc-text-field--with-trailing-icon .mdc-text-field__input,
  .text-field-resizable .mdc-text-field--with-trailing-icon .mdc-text-field__input {
    padding-right: 56px;
  }
  .text-field-resizable .trailing-icon {
    right: 0;
  }

  // textarea
  .text-field-resizable .mdc-text-field--textarea .mdc-text-field__input {
    height: 48px;
  }
  .text-field-resizable .text-field-resizable {
    position: relative;
  }
  .text-field-resizable .mdc-text-field--textarea:not(.mdc-text-field--disabled) {
    @include mdc-text-field-fill-color(whitesmoke)
  }
  .text-field-resizable .mdc-text-field--textarea.mdc-notched-outline--upgraded .mdc-floating-label--float-above,
  .text-field-resizable .mdc-text-field--textarea .mdc-notched-outline--upgraded .mdc-floating-label--float-above {
    transform: translateY(-50%) scale(0.75);
    top: 16px;
  }
  .text-field-resizable .mdc-text-field--textarea .mdc-text-field__input {
    padding: 12px 16px 6px;
  }
  .resizable-textarea .mdc-text-field--textarea.mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-notched-outline__leading,
  .resizable-textarea .mdc-text-field--textarea.mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-notched-outline__notch,
  .resizable-textarea .mdc-text-field--textarea.mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-notched-outline__trailing {
    border-color: $mdc-theme-primary;
    border-width: 1px;
  }
  .resizable-textarea .mdc-text-field--textarea.mdc-text-field--focused.mdc-text-field--invalid .mdc-notched-outline__leading,
  .resizable-textarea .mdc-text-field--textarea.mdc-text-field--focused.mdc-text-field--invalid .mdc-notched-outline__notch,
  .resizable-textarea .mdc-text-field--textarea.mdc-text-field--focused.mdc-text-field--invalid .mdc-notched-outline__trailing {
    border-color: $mdc-theme-error;
    border-width: 1px;
  }
  .resizable-textarea .mdc-notched-outline__leading,
  .resizable-textarea .mdc-notched-outline--notched,
  .resizable-textarea .mdc-notched-outline--notch,
  .resizable-textarea .mdc-notched-outline__trailing {
    border-left: none;
    border-right: none;
    border-top: none;
  }
  .text-field-resizable .mdc-text-field--textarea .mdc-notched-outline .mdc-notched-outline__leading {
    border-radius: 4px 0 0 0;
  }
  .text-field-resizable .mdc-text-field--textarea .mdc-notched-outline .mdc-notched-outline__trailing {
    border-radius: 0;
  }
  .text-field-resizable .mdc-text-field--textarea .mdc-notched-outline__leading,
  .text-field-resizable .mdc-text-field--textarea .mdc-notched-outline__notch,
  .text-field-resizable .mdc-text-field--textarea .mdc-notched-outline__trailing {
    border-top: none;
  }
  .resizable-textarea .mdc-text-field--textarea:hover:not(.mdc-text-field--disabled) {
    background-color: $input-background--hover;
  }
  .resizable-textarea .mdc-text-field--focused:hover:not(.mdc-text-field--disabled) {
    background-color: $input-background--focused;
  }
  .resizable-textarea .mdc-text-field--focused:not(.mdc-text-field--disabled) {
    background-color: $input-background--focused;
  }
  .resizable-textarea .mdc-text-field .mdc-text-field__input {
    line-height: 20px;
    padding-top: 16px;
  }

  // Disabled styles
  .text-field-resizable[disabled=disabled] .mdc-text-field--textarea .mdc-floating-label {
    color: $black-medium-emphasis;
  }
  .text-field-resizable[disabled=disabled]:not(.mdc-text-field--disabled):not(.mdc-text-field--outlined):not(.mdc-text-field--textarea) .mdc-text-field__input {
    border-bottom-color: $black-disabled;
  }
  .text-field-resizable[disabled=disabled] .mdc-text-field__input{
    border-bottom: 1px solid $black-disabled;
  }
  .text-field-resizable[disabled=disabled] .mdc-text-field--disabled .mdc-text-field__input {
    color: $black-high-emphasis;
    margin-bottom: 0;
  }

  // Animations
  .resizable-textarea .mdc-text-field .mdc-floating-label--shake {
    animation: shake 0.3s both;
    top: 13.5px;
  }
  .resizable-textarea textarea {
    resize: none !important;
  }
  @keyframes shake {
    0% {
      transform: translateX(0) scale(0.75) translateY(-50%);
    }
    33% {
      animation-timing-function: cubic-bezier(.5, 0, .701732, .495819);
      transform: translateX(4%) scale(0.75) translateY(-50%);
    }
    66% {
      animation-timing-function: cubic-bezier(.302435, .381352, .55, .956352);
      transform: translateX(-4%) scale(0.75) translateY(-50%);
    }
    100% {
      transform: translateX(0) scale(0.75) translateY(-50%);
    }
  }

// Variant input fields
/deep/ input[type="date"] {
  position: relative;
}
/deep/ input[type="date"]::-webkit-calendar-picker-indicator {
  background: transparent;
  bottom: 0;
  color: transparent;
  cursor: pointer;
  height: auto;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: auto;
  z-index: 9;
}
</style>
